/**
 * The galaxy view (GDD §8.1): a spiral galaxy on a tilted disc. Two
 * logarithmic arms of dust and nebulae wind out from a glowing core where the
 * Foundry sits, and the galaxy's planets lie along those arms as shaded
 * spheres. Ships run dotted lanes back to the core.
 *
 * Everything static (dust, nebulae, planets, lanes) is built once or redrawn
 * only when ownership or selection changes; ships, rings and twinkles redraw
 * every frame on one Graphics.
 */

import { Application, Circle, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import { PLANETS } from '../content/planets';
import { resource } from '../content/resources';
import { visiblePlanets } from '../sim/projects';
import { cargoCap, unlockCost } from '../sim/economy';
import type { FleetStats } from '../sim/stats';
import type { GameState } from '../sim/state';
import { fmtNum } from './format';

const FOUNDRY_RADIUS = 26;

const CYAN = 0x4dd9f5;
const VIOLET = 0x9b7bff;
const GOLD = 0xf5c542;

/** The disc is seen at an angle: y is flattened. */
const TILT = 0.62;
/** Log-spiral arm shape: θ = arm·π + ln(r / R0) · WIND. */
const ARM_R0 = 34;
const ARM_WIND = 1.7;
const GALAXY_RADIUS = 980;
/** Planets with rings, for variety. */
const RINGED = new Set(['titans_rest', 'umbral_eclipse', 'event_horizon', 'cobalt_reach']);

function armAngle(arm: number, r: number): number {
  return arm * Math.PI + Math.log(Math.max(r, ARM_R0) / ARM_R0) * ARM_WIND;
}

function onDisc(r: number, angle: number): { x: number; y: number } {
  return { x: Math.cos(angle) * r, y: Math.sin(angle) * r * TILT };
}

/**
 * Planets alternate between the two arms, moving outward, with a fixed nudge
 * each so the galaxy reads as a hand-drawn constellation rather than a ruled
 * spiral.
 */
const PLANET_NUDGE: readonly (readonly [number, number])[] = [
  [0, 0.1], [6, -0.2], [-8, 0.25], [10, -0.1], [-6, 0.3], [12, -0.28],
  [-10, 0.18], [8, -0.32], [-12, 0.22], [14, -0.15], [-8, 0.28], [10, -0.24],
];

export function planetPos(index: number): { x: number; y: number } {
  const [dr, da] = PLANET_NUDGE[index % PLANET_NUDGE.length]!;
  const r = 104 + index * 32 + dr;
  return onDisc(r, armAngle(index % 2, r) + da);
}

/** Constellation links: each planet to the previous one on its arm; the first two to the core. */
export function constellationLinks(): readonly (readonly [number, number])[] {
  return PLANETS.map((_, i) => [i, i >= 2 ? i - 2 : -1] as const);
}

// ── Procedural textures ────────────────────────────────────────────────────

function canvasTexture(size: number, paint: (ctx: CanvasRenderingContext2D, s: number) => void): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  paint(canvas.getContext('2d')!, size);
  return Texture.from(canvas);
}

/** A soft white radial glow; tinted and additively blended for cores and nebulae. */
function glowTexture(): Texture {
  return canvasTexture(128, (ctx, s) => {
    const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.55)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.12)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, s, s);
  });
}

/** A lit sphere in greys, lit from the upper left; the sprite tint gives it its colour. */
function sphereTexture(): Texture {
  return canvasTexture(128, (ctx, s) => {
    const c = s / 2;
    ctx.beginPath();
    ctx.arc(c, c, c - 1, 0, Math.PI * 2);
    ctx.clip();
    const body = ctx.createRadialGradient(s * 0.36, s * 0.32, s * 0.04, c, c, c);
    body.addColorStop(0, '#ffffff');
    body.addColorStop(0.35, '#c9c9c9');
    body.addColorStop(0.75, '#4a4a4a');
    body.addColorStop(1, '#111111');
    ctx.fillStyle = body;
    ctx.fillRect(0, 0, s, s);
    // Faint bands, so worlds read as surfaces rather than balls.
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = '#000000';
    for (const [y, h] of [[0.3, 0.05], [0.48, 0.08], [0.66, 0.04]] as const) ctx.fillRect(0, s * y, s, s * h);
  });
}

// ── Dust ───────────────────────────────────────────────────────────────────

function seeded(seed: number): () => number {
  let x = seed;
  return () => { x = (x * 16807) % 2147483647; return x / 2147483647; };
}

function gauss(rand: () => number): number {
  return Math.sqrt(-2 * Math.log(Math.max(1e-9, rand()))) * Math.cos(2 * Math.PI * rand());
}

const DUST_COLORS = [0xffffff, 0xcfe6ff, 0x7fe6ff, 0x9b7bff, 0xc7a7ff, 0xff7aab];

function buildDust(g: Graphics): void {
  const rand = seeded(1337);
  // Arm dust: dense near the core, warm inside and cool further out.
  for (let i = 0; i < 2800; i += 1) {
    const arm = i % 2;
    const r = ARM_R0 + GALAXY_RADIUS * Math.pow(rand(), 0.85);
    const spread = 0.12 + 0.12 * (r / GALAXY_RADIUS);
    const angle = armAngle(arm, r) + gauss(rand) * spread;
    const { x, y } = onDisc(r * (1 + gauss(rand) * 0.03), angle);
    const inner = r < 110;
    const color = inner ? (rand() < 0.5 ? 0xffe7b0 : 0xffffff) : DUST_COLORS[Math.floor(rand() * DUST_COLORS.length)]!;
    g.circle(x, y, 0.5 + rand() * 1.2).fill({ color, alpha: 0.12 + rand() * 0.4 });
  }
  // A faint halo of disc stars between the arms.
  for (let i = 0; i < 450; i += 1) {
    const r = GALAXY_RADIUS * Math.sqrt(rand());
    const { x, y } = onDisc(r, rand() * Math.PI * 2);
    g.circle(x, y, 0.4 + rand() * 0.7).fill({ color: 0xcfe6ff, alpha: 0.04 + rand() * 0.1 });
  }
  // Bright glint stars scattered through the disc: the star-chart look.
  for (let i = 0; i < 110; i += 1) {
    const r = 60 + GALAXY_RADIUS * Math.sqrt(rand());
    const { x, y } = onDisc(r, rand() * Math.PI * 2);
    const size = 2 + rand() * 3.5;
    const color = DUST_COLORS[Math.floor(rand() * DUST_COLORS.length)]!;
    const alpha = 0.35 + rand() * 0.4;
    g.moveTo(x - size, y).lineTo(x + size, y).stroke({ width: 0.8, color, alpha });
    g.moveTo(x, y - size).lineTo(x, y + size).stroke({ width: 0.8, color, alpha });
    g.circle(x, y, 1.1).fill({ color: 0xffffff, alpha: Math.min(1, alpha + 0.3) });
  }
}

interface PlanetNode {
  readonly g: Graphics;
  readonly body: Sprite;
  readonly halo: Sprite;
  readonly label: Text;
  readonly tag: Text;
}

export class GalaxyView {
  readonly app = new Application();
  private readonly world = new Container();
  private readonly stars = new Graphics();
  private readonly dust = new Graphics();
  private readonly nebulae = new Container();
  private readonly lanes = new Graphics();
  private readonly core = new Container();
  private readonly planetLayer = new Container();
  private readonly fx = new Graphics();
  private readonly nodes: PlanetNode[] = [];
  private coreGlow!: Sprite;
  private discGlow!: Sprite;
  private twinkles: { x: number; y: number; phase: number }[] = [];
  private zoom = 1;
  /**
   * The player's camera on top of the auto-fit: a zoom multiplier and a pan
   * centre in galaxy units. Targets are eased toward, so buttons, pinches and
   * zoom-to-planet all glide.
   */
  private userZoom = 1;
  private targetZoom = 1;
  private pan = { x: 0, y: 0 };
  private targetPan = { x: 0, y: 0 };
  /** Pointer travel this gesture; a tap that dragged is not a tap. */
  private dragged = 0;
  private staticKey = '';
  private starKey = '';

  private readonly asteroidG = new Graphics();

  constructor(
    private readonly host: HTMLElement,
    private readonly onTap: (index: number) => void,
    private readonly onAsteroid: () => void,
  ) {}

  async init(): Promise<void> {
    await this.app.init({
      resizeTo: this.host,
      background: '#03050b',
      antialias: true,
      autoDensity: true,
      resolution: Math.min(2, window.devicePixelRatio || 1),
    });
    this.host.appendChild(this.app.canvas);
    // resizeTo only hears window resizes; the strip also changes size when tabs show and hide it.
    new ResizeObserver(() => this.app.resize()).observe(this.host);
    this.bindCamera();

    const glow = glowTexture();
    const sphere = sphereTexture();

    this.app.stage.addChild(this.stars, this.world);
    this.world.addChild(this.nebulae, this.dust, this.lanes, this.core, this.planetLayer, this.fx, this.asteroidG);
    this.asteroidG.eventMode = 'static';
    this.asteroidG.cursor = 'pointer';
    this.asteroidG.on('pointertap', () => { if (this.dragged < 8) this.onAsteroid(); });

    // The galactic disc and core.
    this.discGlow = new Sprite({ texture: glow, anchor: 0.5, tint: 0x6a4fd0, alpha: 0.22, blendMode: 'add' });
    this.discGlow.scale.set((GALAXY_RADIUS * 1.5) / 64, (GALAXY_RADIUS * 1.5 * TILT) / 64);
    this.coreGlow = new Sprite({ texture: glow, anchor: 0.5, tint: 0xffe2a8, alpha: 0.85, blendMode: 'add' });
    this.coreGlow.scale.set(110 / 64, (110 * 0.7) / 64);
    this.nebulae.addChild(this.discGlow, this.coreGlow);

    // Nebulae along the arms.
    const rand = seeded(99);
    const tints = [0x9b7bff, 0xff3d7f, 0x4dd9f5, 0x7b6bf0, 0xff7aab];
    for (let i = 0; i < 16; i += 1) {
      const r = 90 + rand() * (GALAXY_RADIUS - 110);
      const { x, y } = onDisc(r, armAngle(i % 2, r) + gauss(rand) * 0.08);
      const s = new Sprite({ texture: glow, anchor: 0.5, tint: tints[i % tints.length]!, alpha: 0.14 + rand() * 0.12, blendMode: 'add' });
      const size = 55 + rand() * 70;
      s.position.set(x, y);
      s.scale.set(size / 64, (size * (0.6 + rand() * 0.3)) / 64);
      s.rotation = (rand() - 0.5) * 0.6;
      this.nebulae.addChild(s);
    }

    buildDust(this.dust);
    this.buildCore();

    const twinkleRand = seeded(4242);
    this.twinkles = Array.from({ length: 36 }, () => {
      const r = 60 + twinkleRand() * GALAXY_RADIUS;
      const { x, y } = onDisc(r, armAngle(Math.floor(twinkleRand() * 2), r) + gauss(twinkleRand) * 0.3);
      return { x, y, phase: twinkleRand() * Math.PI * 2 };
    });

    for (const def of PLANETS) {
      const { x, y } = planetPos(def.index);
      const halo = new Sprite({ texture: glow, anchor: 0.5, alpha: 0.35, blendMode: 'add' });
      halo.position.set(x, y);
      halo.scale.set((def.size * 3.2) / 128);
      const body = new Sprite({ texture: sphere, anchor: 0.5 });
      body.position.set(x, y);
      body.scale.set((def.size * 2) / 128);
      const g = new Graphics();
      g.eventMode = 'static';
      g.cursor = 'pointer';
      g.hitArea = new Circle(x, y, def.size + 12);
      g.on('pointertap', () => { if (this.dragged < 8) this.onTap(def.index); });
      const label = new Text({ text: def.name, style: { fontFamily: 'Segoe UI', fontSize: 11, fill: '#b9c6e0', fontWeight: '600', dropShadow: { color: '#000000', blur: 3, distance: 0, alpha: 0.9 } } });
      const tag = new Text({ text: '', style: { fontFamily: 'Segoe UI', fontSize: 11, fill: '#f5c542', fontWeight: '700', dropShadow: { color: '#000000', blur: 3, distance: 0, alpha: 0.9 } } });
      for (const t of [label, tag]) { t.anchor.set(0.5, 0); t.resolution = 2; t.eventMode = 'none'; }
      this.planetLayer.addChild(halo, body, g, label, tag);
      this.nodes.push({ g, body, halo, label, tag });
    }
  }

  private buildCore(): void {
    const g = new Graphics();
    g.circle(0, 0, FOUNDRY_RADIUS).stroke({ width: 3, color: CYAN, alpha: 0.95 });
    g.circle(0, 0, FOUNDRY_RADIUS - 8).stroke({ width: 2, color: VIOLET, alpha: 0.85 });
    // The IdleVoid prism: a pale hex with a violet and a blue facet meeting at the base.
    const r = 11;
    const c = Math.cos(Math.PI / 6) * r;
    const top = [0, -r], ur = [c, -r / 2], lr = [c, r / 2], bot = [0, r], ll = [-c, r / 2], ul = [-c, -r / 2];
    g.poly([...ul, ...top, ...ur, ...bot]).fill({ color: 0xd8f8ff });
    g.poly([...ul, ...bot, ...ll]).fill({ color: 0x8f4fff });
    g.poly([...ur, ...lr, ...bot]).fill({ color: 0x24a9e6 });
    this.core.addChild(g);
  }

  private drawStars(w: number, h: number): void {
    const key = `${w}x${h}`;
    if (key === this.starKey) return;
    this.starKey = key;
    this.stars.clear();
    const rand = seeded(7);
    for (let i = 0; i < Math.floor((w * h) / 1800); i += 1) {
      this.stars.circle(rand() * w, rand() * h, rand() < 0.08 ? 1.1 : 0.6).fill({ color: 0xffffff, alpha: 0.1 + rand() * 0.3 });
    }
  }

  private drawStatic(state: GameState, stats: FleetStats, selected: number): void {
    // Every planet the Telescopes reveal is drawn; owned ones bright, the rest dim with a price.
    const range = visiblePlanets(state);
    const seen = (i: number) => i < range || state.run.planets[i]!.owned;
    const prices = PLANETS.map((_, i) => (state.run.planets[i]!.owned || !seen(i) ? 0 : Math.round(unlockCost(state, stats, i))));
    const key = `${state.run.planets.map((p) => (p.owned ? 1 : 0)).join('')}|${range}|${selected}|${prices.join(',')}`;
    if (key === this.staticKey) return;
    this.staticKey = key;
    this.lanes.clear();
    // Constellation lines between neighbouring worlds; brighter once both ends are yours.
    for (const [a, b] of constellationLinks()) {
      const pa = state.run.planets[a]!;
      const showA = seen(a);
      const showB = b < 0 || seen(b);
      if (!showA || !showB) continue;
      const from = planetPos(a);
      const to = b < 0 ? { x: 0, y: 0 } : planetPos(b);
      const ownedBoth = pa.owned && (b < 0 || state.run.planets[b]!.owned);
      const d = Math.hypot(to.x - from.x, to.y - from.y);
      const ux = (to.x - from.x) / d, uy = (to.y - from.y) / d;
      const startGap = PLANETS[a]!.size + 6;
      const endGap = b < 0 ? FOUNDRY_RADIUS + 8 : PLANETS[b]!.size + 6;
      if (ownedBoth) {
        this.lanes.moveTo(from.x + ux * startGap, from.y + uy * startGap)
          .lineTo(to.x - ux * endGap, to.y - uy * endGap)
          .stroke({ width: 1.3, color: 0xa8f2ff, alpha: 0.5 });
      } else {
        // Unclaimed links are dashed.
        for (let t = startGap; t < d - endGap; t += 10) {
          const e = Math.min(t + 5, d - endGap);
          this.lanes.moveTo(from.x + ux * t, from.y + uy * t).lineTo(from.x + ux * e, from.y + uy * e)
            .stroke({ width: 1, color: 0xa8f2ff, alpha: 0.22 });
        }
      }
    }
    for (const def of PLANETS) {
      const node = this.nodes[def.index]!;
      const owned = state.run.planets[def.index]!.owned;
      const visible = seen(def.index);
      for (const part of [node.g, node.body, node.halo, node.label, node.tag]) part.visible = visible;
      node.g.clear();
      if (!visible) continue;
      const { x, y } = planetPos(def.index);
      const color = resource(def.ores[def.ores.length - 1]!.id).color;
      if (owned) {
        // A dotted route from the core to the planet.
        const d = Math.hypot(x, y);
        const steps = Math.floor((d - FOUNDRY_RADIUS - def.size) / 7);
        for (let k = 1; k < steps; k += 1) {
          const t = (FOUNDRY_RADIUS + k * 7) / d;
          this.lanes.circle(x * t, y * t, 0.8).fill({ color: CYAN, alpha: 0.16 });
        }
        node.body.tint = color;
        node.body.alpha = 1;
        node.halo.tint = color;
        node.halo.alpha = 0.4;
        node.tag.text = '';
        node.label.style.fill = '#b9c6e0';
      } else {
        node.body.tint = 0x2b3550;
        node.body.alpha = 0.85;
        node.halo.tint = color;
        node.halo.alpha = 0.12;
        node.g.circle(x, y, def.size + 3).stroke({ width: 1, color, alpha: 0.45 });
        node.tag.text = `◈ ${fmtNum(prices[def.index]!)}`;
        node.label.style.fill = '#7d8cab';
      }
      if (RINGED.has(def.id)) {
        node.g.ellipse(x, y, def.size * 1.75, def.size * 0.45).stroke({ width: 2, color, alpha: owned ? 0.55 : 0.2 });
      }
      if (def.index === selected) node.g.circle(x, y, def.size + 6).stroke({ width: 1.5, color: 0xffffff, alpha: 0.7 });
    }
  }

  private fit(state: GameState, w: number, h: number): void {
    const furthest = Math.max(visiblePlanets(state), state.run.planets.reduce((m, p, i) => (p.owned ? i + 1 : m), 0)) - 1;
    // Worlds scale with the zoom but labels stay a fixed pixel size, so solve
    // for the largest zoom that keeps both inside the strip: |x|·z + px ≤ w/2.
    const pad = 6;
    // Horizontal room for the zoom buttons on the right edge (kept symmetric so the core stays centred).
    const padX = 50;
    let target = Math.min(2.2, (w / 2 - padX) / (FOUNDRY_RADIUS + 40), (h / 2 - pad) / (FOUNDRY_RADIUS + 30));
    for (let i = 0; i <= furthest; i += 1) {
      const p = planetPos(i);
      const def = PLANETS[i]!;
      const node = this.nodes[i]!;
      const size = def.size * (RINGED.has(def.id) ? 1.75 : 1);
      const labelHalf = (node.label.width / node.label.scale.x) / 2;
      const labelsBelow = node.tag.text ? 30 : 16;
      target = Math.min(
        target,
        (w / 2 - padX) / (Math.abs(p.x) + size),
        (w / 2 - padX - labelHalf + 40) / Math.max(1, Math.abs(p.x)),
        p.y > 0 ? (h / 2 - pad - labelsBelow) / (p.y + def.size + 4) : (h / 2 - pad) / (Math.abs(p.y) + size),
      );
    }
    this.zoom += (target - this.zoom) * 0.08;
    this.userZoom += (this.targetZoom - this.userZoom) * 0.2;
    this.pan.x += (this.targetPan.x - this.pan.x) * 0.2;
    this.pan.y += (this.targetPan.y - this.pan.y) * 0.2;
    const scale = this.zoom * this.userZoom;
    this.world.position.set(w / 2 - this.pan.x * scale, h / 2 - this.pan.y * scale);
    this.world.scale.set(scale);
    for (const def of PLANETS) {
      const node = this.nodes[def.index]!;
      const { x, y } = planetPos(def.index);
      const s = 1 / scale;
      node.label.scale.set(s);
      node.tag.scale.set(s);
      node.label.position.set(x, y + def.size + 8 + 2 * s);
      node.tag.position.set(x, y + def.size + 8 + 16 * s);
    }
  }

  /**
   * Keep planet names readable: nudge each label inside the strip (clear of
   * the zoom buttons), and where two would overlap, hide the less important
   * one: the selected planet, then the next one to buy, then owned (newest
   * first), then the rest.
   */
  private placeLabels(state: GameState, w: number, h: number, selected: number): void {
    const scale = this.world.scale.x;
    const toScreenX = (x: number) => this.world.position.x + x * scale;
    const next = state.run.planets.findIndex((p) => !p.owned);
    const rank = (i: number) => (i === selected ? 0 : i === next ? 1 : state.run.planets[i]!.owned ? 2 + (PLANETS.length - i) / 100 : 3 + i / 100);
    const order = PLANETS.map((d) => d.index).filter((i) => this.nodes[i]!.body.visible).sort((a, b) => rank(a) - rank(b));
    type Box = { l: number; r: number; t: number; b: number };
    const placed: Box[] = [];
    const hits = (a: Box, o: Box) => a.l < o.r + 2 && a.r > o.l - 2 && a.t < o.b + 1 && a.b > o.t - 1;
    // Other planets' spheres are obstacles too, so a name never runs across a world.
    const bodies: { i: number; box: Box }[] = order.map((i) => {
      const { x, y } = planetPos(i);
      const r = PLANETS[i]!.size * scale;
      const cx = toScreenX(x);
      const cy = this.world.position.y + y * scale;
      return { i, box: { l: cx - r, r: cx + r, t: cy - r, b: cy + r } };
    });
    const left = 6;
    const right = w - 52;
    for (const i of order) {
      const node = this.nodes[i]!;
      const def = PLANETS[i]!;
      const { x, y } = planetPos(i);
      const half = Math.max(node.label.width, node.tag.text ? node.tag.width : 0) * scale / 2;
      const sx = toScreenX(x);
      const shift = Math.max(left + half - sx, Math.min(0, right - half - sx));
      const lx = x + shift / scale;
      node.label.position.x = lx;
      node.tag.position.x = lx;
      const top = this.world.position.y + (y + def.size + 8) * scale;
      const box = { l: sx + shift - half, r: sx + shift + half, t: top, b: top + (node.tag.text ? 30 : 15) };
      // A planet off the strip (zoomed or panned away) keeps its name hidden rather than pinned to the edge.
      const sy = this.world.position.y + y * scale;
      const offscreen = sx < 0 || sx > w || sy < 0 || sy > h;
      const vip = rank(i) < 2;
      const clash = offscreen || (!vip && (box.b > h
        || placed.some((o) => hits(box, o))
        || bodies.some((o) => o.i !== i && hits(box, o.box))));
      node.label.visible = !clash;
      node.tag.visible = !clash;
      if (!clash) placed.push(box);
    }
  }

  private triangle(x: number, y: number, angle: number, size: number, color: number, alpha = 1): void {
    const pts: number[] = [];
    for (const [a, r] of [[0, size], [2.5, size * 0.8], [-2.5, size * 0.8]] as const) {
      pts.push(x + Math.cos(angle + a) * r, y + Math.sin(angle + a) * r);
    }
    this.fx.poly(pts).fill({ color, alpha });
  }

  // ── Camera ───────────────────────────────────────────────────────────────

  static readonly ZOOM_MAX = 6;

  /** Zoom by a factor, keeping the galaxy point under (sx, sy) on screen still. */
  private zoomAt(factor: number, sx: number, sy: number): void {
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    const before = this.zoom * this.targetZoom;
    const next = Math.max(1, Math.min(GalaxyView.ZOOM_MAX, this.targetZoom * factor));
    const after = this.zoom * next;
    // The world point under the cursor before and after must match.
    const wx = this.targetPan.x + (sx - w / 2) / before;
    const wy = this.targetPan.y + (sy - h / 2) / before;
    this.targetZoom = next;
    this.targetPan = this.clampPan(wx - (sx - w / 2) / after, wy - (sy - h / 2) / after);
  }

  private clampPan(x: number, y: number): { x: number; y: number } {
    // At ×1 the view is centred; zooming in lets you travel out to the rim.
    const reach = GALAXY_RADIUS * (1 - 1 / this.targetZoom);
    return {
      x: Math.max(-reach, Math.min(reach, x)),
      y: Math.max(-reach * TILT, Math.min(reach * TILT, y)),
    };
  }

  /** Buttons: zoom about the centre of the view. */
  zoomBy(factor: number): void {
    this.zoomAt(factor, this.app.screen.width / 2, this.app.screen.height / 2);
  }

  /** Glide to a planet, zooming in if the camera is still wide. */
  focusPlanet(index: number): void {
    this.targetZoom = Math.max(this.targetZoom, 2.5);
    const p = planetPos(index);
    this.targetPan = this.clampPan(p.x, p.y);
  }

  resetCamera(): void {
    this.targetZoom = 1;
    this.targetPan = { x: 0, y: 0 };
  }

  get zoomed(): boolean {
    return this.targetZoom > 1.01;
  }

  private bindCamera(): void {
    const canvas = this.app.canvas;
    canvas.style.touchAction = 'none';
    const pointers = new Map<number, { x: number; y: number }>();
    let last: { x: number; y: number; dist: number } | null = null;
    const local = (e: PointerEvent | WheelEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const centroid = () => {
      const ps = [...pointers.values()];
      const x = ps.reduce((a, p) => a + p.x, 0) / ps.length;
      const y = ps.reduce((a, p) => a + p.y, 0) / ps.length;
      const dist = ps.length > 1 ? Math.hypot(ps[0]!.x - ps[1]!.x, ps[0]!.y - ps[1]!.y) : 0;
      return { x, y, dist };
    };
    canvas.addEventListener('pointerdown', (e) => {
      pointers.set(e.pointerId, local(e));
      if (pointers.size === 1) this.dragged = 0;
      last = centroid();
    });
    canvas.addEventListener('pointermove', (e) => {
      if (!pointers.has(e.pointerId) || !last) return;
      pointers.set(e.pointerId, local(e));
      const now = centroid();
      const scale = this.zoom * this.targetZoom;
      this.dragged += Math.hypot(now.x - last.x, now.y - last.y);
      if (pointers.size >= 2 && last.dist > 0 && now.dist > 0) {
        this.zoomAt(now.dist / last.dist, now.x, now.y);
        this.dragged += 99;
      }
      // Drag pans (with one finger or two), snapping instantly so it tracks the finger.
      this.targetPan = this.clampPan(this.targetPan.x - (now.x - last.x) / scale, this.targetPan.y - (now.y - last.y) / scale);
      this.pan = { ...this.targetPan };
      last = now;
    });
    const end = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      last = pointers.size ? centroid() : null;
    };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    canvas.addEventListener('pointerleave', end);
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const at = local(e);
      this.zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, at.x, at.y);
    }, { passive: false });
    canvas.addEventListener('dblclick', (e) => {
      const at = local(e as unknown as PointerEvent);
      this.zoomAt(this.zoomed ? 1 / this.targetZoom : 2.5, at.x, at.y);
    });
  }

  draw(state: GameState, stats: FleetStats, selected: number): void {
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    if (w <= 0 || h <= 0) return;
    const now = performance.now() / 1000;
    this.drawStars(w, h);
    this.drawStatic(state, stats, selected);
    this.fit(state, w, h);
    this.placeLabels(state, w, h, selected);

    // The core breathes; the disc glow drifts very slowly.
    this.coreGlow.alpha = 0.75 + Math.sin(now * 1.2) * 0.1;
    this.discGlow.rotation = Math.sin(now * 0.05) * 0.04;

    const fx = this.fx;
    fx.clear();

    for (const t of this.twinkles) {
      const a = 0.5 + 0.5 * Math.sin(now * 2.2 + t.phase);
      if (a > 0.55) fx.circle(t.x, t.y, 0.8 + a).fill({ color: 0xffffff, alpha: (a - 0.5) * 1.6 });
    }

    // Ships on their lanes.
    state.run.planets.forEach((p, i) => {
      if (!p.owned) return;
      const def = PLANETS[i]!;
      const { x, y } = planetPos(i);
      const d = Math.hypot(x, y);
      const ux = x / d, uy = y / d;
      const sx = ux * (FOUNDRY_RADIUS + 3), sy = uy * (FOUNDRY_RADIUS + 3);
      const ex = x - ux * (def.size + 5), ey = y - uy * (def.size + 5);
      let px: number, py: number, angle: number;
      if (p.shipPhase === 'out') {
        px = sx + (ex - sx) * p.shipT; py = sy + (ey - sy) * p.shipT;
        angle = Math.atan2(uy, ux);
      } else {
        px = ex + (sx - ex) * p.shipT; py = ey + (sy - ey) * p.shipT;
        angle = Math.atan2(-uy, -ux);
      }
      const loaded = p.shipPhase === 'back' && p.shipLoad > 0;
      // A short engine trail behind the ship.
      fx.moveTo(px, py).lineTo(px - Math.cos(angle) * 9, py - Math.sin(angle) * 9).stroke({ width: 1.6, color: loaded ? GOLD : CYAN, alpha: 0.35 });
      this.triangle(px, py, angle, 4.2, loaded ? 0xffffff : CYAN, loaded ? 1 : 0.9);
      if (loaded) fx.circle(px - Math.cos(angle) * 4, py - Math.sin(angle) * 4, 1.6).fill({ color: GOLD });

      // A stockpile ring fills as ore waits for the ship.
      const fill = Math.min(1, p.stock / Math.max(1e-9, cargoCap(state, stats, i)));
      if (fill > 0.02) {
        // arc() joins from the pen's last point (the ship); start a fresh subpath at the arc's start.
        fx.moveTo(x, y - def.size - 4).arc(x, y, def.size + 4, -Math.PI / 2, -Math.PI / 2 + fill * Math.PI * 2).stroke({ width: 2, color: GOLD, alpha: 0.8 });
      }

      // Mining drones circling the planet: purely a sign that it is mining.
      // More Mining levels, more drones (1-3). The orbit is flattened like the
      // galaxy's tilt, and a drone dims as it passes behind the planet.
      const drones = 1 + (p.levels.mine >= 10 ? 1 : 0) + (p.levels.mine >= 25 ? 1 : 0);
      const ore = resource(def.ores[def.ores.length - 1]!.id).color;
      // Drones keep a readable on-screen size whatever the zoom.
      const px1 = 1 / this.world.scale.x;
      const rx = def.size + Math.max(10, 7 * px1);
      const ry = rx * 0.5;
      const speed = 0.7 + (i % 5) * 0.08;
      for (let k = 0; k < drones; k += 1) {
        const t = now * speed + (k * Math.PI * 2) / drones + i * 1.7;
        const dx = x + Math.cos(t) * rx;
        const dy = y + Math.sin(t) * ry;
        const behind = Math.sin(t) < 0;
        // Hidden while the planet's sphere is in front of it.
        if (behind && Math.hypot(dx - x, dy - y) < def.size + 1.5) continue;
        const heading = Math.atan2(Math.cos(t) * ry, -Math.sin(t) * rx);
        // A brief mining beam to the surface, on and off.
        if (!behind && Math.sin(now * 3 + k * 2 + i) > 0.55) {
          const bx = x + Math.cos(t) * def.size * 0.7;
          const by = y + Math.sin(t) * def.size * 0.35;
          fx.moveTo(dx, dy).lineTo(bx, by).stroke({ width: Math.max(1, 1.2 * px1), color: ore, alpha: 0.6 });
        }
        this.triangle(dx, dy, heading, Math.max(3, 3.4 * px1), behind ? 0x7d8cab : 0xdff6ff, behind ? 0.35 : 0.95);
      }
    });

    // A drifting asteroid, tappable for a burst of ore.
    const a = state.run.asteroid;
    this.asteroidG.clear();
    this.asteroidG.visible = !!a;
    if (a) {
      const pulse = 0.5 + 0.5 * Math.sin(now * 4);
      const rot = now * 0.8;
      const pts: number[] = [];
      for (let k = 0; k < 7; k += 1) {
        const ang = rot + (k / 7) * Math.PI * 2;
        const rr = 11 * (0.75 + 0.25 * Math.sin(k * 2.3));
        pts.push(a.x + Math.cos(ang) * rr, a.y + Math.sin(ang) * rr);
      }
      this.asteroidG.circle(a.x, a.y, 20 + pulse * 4).fill({ color: GOLD, alpha: 0.14 });
      this.asteroidG.circle(a.x, a.y, 16 + pulse * 6).stroke({ width: 1.5, color: GOLD, alpha: 0.6 - pulse * 0.4 });
      this.asteroidG.poly(pts).fill({ color: 0x8a7a66 }).stroke({ width: 1.5, color: GOLD, alpha: 0.9 });
      this.asteroidG.hitArea = new Circle(a.x, a.y, 30);
    }

    // Smelters and crafters at work: violet sparks orbit the core.
    const busy = state.run.smelters.concat(state.run.crafters).filter((s) => s.running).length;
    for (let k = 0; k < busy; k += 1) {
      const a = now * 1.4 + (k * Math.PI * 2) / Math.max(1, busy);
      fx.circle(Math.cos(a) * (FOUNDRY_RADIUS + 6), Math.sin(a) * (FOUNDRY_RADIUS + 6), 1.8).fill({ color: VIOLET });
    }
  }
}
